import { isValidHttpUrl } from '@Utils/helpers/restHelper';
import { Dialog, styled } from '@mui/material';
import _isNil from 'lodash/isNil';
import React, { useEffect, useId, useState } from 'react';
import TextInput from '@Components/DomElements/TextInput';
import {
    ModalBody,
    ModalFooter,
    ModalHeader,
} from '@Components/ModalComponents';
import { IProjectDocument } from '@Data/projects/Interfaces';

const DialogBodyDiv = styled('div')`
    width: 500px;
    display: grid;
    grid-column-template: 1fr;
    row-gap: 1em;
`;

interface IManageProjectDocumentDialogProps {
    isOpen: boolean;
    onClose: () => void;
    isSubmitting: boolean;
    onSubmit: (name: string, url: string) => void;
    document?: IProjectDocument;
    id?: string;
}

function hasDocumentChanged(
    name: string,
    url: string,
    existingDocument?: IProjectDocument
) {
    if (_isNil(existingDocument)) {
        return true;
    }

    return name !== existingDocument.name || url !== existingDocument.url;
}

export const ManageProjectDocumentDialog = (
    props: IManageProjectDocumentDialogProps
) => {
    const { isOpen, onClose, isSubmitting, onSubmit, document, id } = props;

    const dialogId = id ?? useId();

    const [documentName, setDocumentName] = useState<string>('');
    const [documentUrl, setDocumentUrl] = useState<string>('');

    const title = `${_isNil(document) ? 'Add New' : 'Edit'} Project Document`;
    const submitText = `${_isNil(document) ? 'Add' : 'Edit'} Document`;

    useEffect(() => {
        if (!_isNil(document)) {
            setDocumentName(document.name);
            setDocumentUrl(document.url);
        } else {
            setDocumentName('');
            setDocumentUrl('');
        }
    }, [document]);

    const handleSubmit = async () => {
        await onSubmit(documentName, documentUrl);
        setDocumentName('');
        setDocumentUrl('');
    };

    const canSubmit =
        !_isNil(documentName) &&
        documentName !== '' &&
        !_isNil(documentUrl) &&
        documentUrl !== '' &&
        isValidHttpUrl(documentUrl) &&
        hasDocumentChanged(documentName, documentUrl, document);

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && canSubmit) {
            handleSubmit();
        }
    };

    return (
        <Dialog id={dialogId} open={isOpen} onClose={onClose}>
            <ModalHeader
                id={`${dialogId}-header`}
                title={title}
                onCancel={onClose}
                isCancelDisabled={isSubmitting}
            />
            <ModalBody>
                <DialogBodyDiv>
                    <TextInput
                        label="Name"
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        autoFocus
                        onKeyUp={handleEnter}
                        disabled={isSubmitting}
                    />
                    <TextInput
                        label="URL"
                        value={documentUrl}
                        onChange={(e) => setDocumentUrl(e.target.value)}
                        onKeyUp={handleEnter}
                        disabled={isSubmitting}
                    />
                </DialogBodyDiv>
            </ModalBody>
            <ModalFooter
                id={`${dialogId}-footer`}
                onCancel={onClose}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                submitText={submitText}
                canSubmit={canSubmit}
            />
        </Dialog>
    );
};
